import { useNavigate } from "react-router-dom";
import { FileText, PlayCircle, ClipboardCheck, ArrowRight } from "lucide-react";

const PROGRAM = [
  { icon: FileText, title: "Training Presentation", desc: "Review the MITRA training presentation for each assigned module." },
  { icon: PlayCircle, title: "Training Video", desc: "Watch the clinical demonstration video for the module." },
  { icon: ClipboardCheck, title: "Assessment", desc: "Complete the 10-question assessment and achieve the required passing score." },
];

const JOURNEY = [
  { n: "01", label: "Join Now", desc: "Enter the MITRA training portal." },
  { n: "02", label: "Create Profile", desc: "Complete your basic details." },
  { n: "03", label: "Request Access", desc: "Request access to your training." },
  { n: "04", label: "Learn", desc: "Review presentations & videos." },
  { n: "05", label: "Assessments", desc: "Complete assigned modules." },
  { n: "06", label: "Onboarding", desc: "Move forward with next steps." },
];

function JoinButton({ onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="inline-flex items-center gap-2 h-12 px-7 rounded-lg bg-[#003d9b] text-white text-[15px] font-semibold tracking-wide hover:bg-[#0052cc] transition-colors"
    >
      JOIN NOW <ArrowRight className="w-4 h-4" />
    </button>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const toAuth = () => navigate("/login");

  return (
    <div className="min-h-screen bg-white text-[#1c1b1b]" data-testid="landing-page">
      {/* HEADER */}
      <header className="border-b border-[#E5E7EB] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8 h-20 flex items-center justify-between">
          <img src="/mitra-logo.png" alt="MITRA — Care That Comes Home" className="h-16 w-auto object-contain" data-testid="header-logo" />
          <button
            data-testid="header-signin-link"
            onClick={toAuth}
            className="text-sm text-[#4B5563] hover:text-[#003d9b] transition-colors font-medium"
          >
            Already have an account? <span className="text-[#003d9b] font-semibold">Sign in →</span>
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <p className="text-[12px] font-bold tracking-[0.14em] uppercase text-[#003d9b] mb-5">MITRA Nurse Training</p>
            <h1 className="text-[40px] sm:text-[48px] lg:text-[54px] font-bold leading-[1.08] tracking-tight">
              Welcome to <span className="text-[#003d9b]">MITRA</span> Training
            </h1>
            <p className="text-[18px] md:text-[20px] text-[#1c1b1b] mt-5 font-medium">
              Your pre-onboarding training program for joining MITRA.
            </p>
            <p className="text-[16px] text-[#4B5563] mt-4 max-w-[30rem] leading-relaxed">
              Build the knowledge and skills needed to deliver care with confidence through MITRA's structured training program.
            </p>
            <div className="mt-9">
              <JoinButton onClick={toAuth} testId="hero-join-btn" />
            </div>
          </div>
          <div className="md:pl-4">
            <img
              src="/hero-nurse.png"
              alt="MITRA nurse caring for an elderly patient at home"
              className="w-full h-auto rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.10)] object-cover"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* YOUR TRAINING PROGRAM */}
      <section className="bg-[#f4f7fb] border-y border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-[28px] md:text-[32px] font-bold tracking-tight">Your Training Program</h2>
            <p className="text-[16px] text-[#4B5563] mt-3">
              Each MITRA training module combines learning, demonstration and assessment.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {PROGRAM.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} data-testid={`program-card-${i}`} className="bg-white border border-[#E5E7EB] rounded-xl p-8 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                  <div className="w-12 h-12 rounded-lg bg-[#003d9b]/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#003d9b]" strokeWidth={2} />
                  </div>
                  <h3 className="text-[18px] font-semibold">{c.title}</h3>
                  <p className="text-[15px] text-[#4B5563] mt-2 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-[14px] text-[#4B5563] mt-10">
            Complete all three components to complete a training module.
          </p>
        </div>
      </section>

      {/* YOUR MITRA TRAINING JOURNEY */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <h2 className="text-[28px] md:text-[32px] font-bold tracking-tight text-center">Your MITRA Training Journey</h2>

          {/* Desktop horizontal timeline */}
          <div className="hidden md:block relative mt-16">
            <div className="absolute top-7 left-[8.33%] right-[8.33%] h-0.5 bg-[#003d9b]/25" />
            <div className="flex">
              {JOURNEY.map((s) => (
                <div key={s.n} data-testid={`journey-step-${s.n}`} className="flex-1 flex flex-col items-center text-center px-3">
                  <div className="w-14 h-14 rounded-full bg-[#003d9b] text-white flex items-center justify-center text-[18px] font-bold relative z-10">
                    {s.n}
                  </div>
                  <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-[#1c1b1b] mt-4">{s.label}</p>
                  <p className="text-[13px] text-[#4B5563] mt-2 leading-snug max-w-[9.5rem]">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile vertical timeline */}
          <div className="md:hidden relative mt-12 pl-1">
            <div className="absolute left-[27px] top-3 bottom-3 w-0.5 bg-[#003d9b]/25" />
            <div className="space-y-8">
              {JOURNEY.map((s) => (
                <div key={s.n} className="flex items-start gap-4 relative">
                  <div className="w-14 h-14 shrink-0 rounded-full bg-[#003d9b] text-white flex items-center justify-center text-[18px] font-bold relative z-10">
                    {s.n}
                  </div>
                  <div className="pt-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-[#1c1b1b]">{s.label}</p>
                    <p className="text-[14px] text-[#4B5563] mt-1 leading-snug">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#f4f7fb] border-t border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-24 text-center">
          <h2 className="text-[28px] md:text-[34px] font-bold tracking-tight">Ready to begin?</h2>
          <div className="mt-8 flex justify-center">
            <JoinButton onClick={toAuth} testId="cta-join-btn" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white border-t border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8 flex items-center justify-between flex-wrap gap-4">
          <img src="/mitra-logo.png" alt="MITRA" className="h-24 w-auto object-contain" data-testid="footer-logo" />
          <span className="text-sm text-[#4B5563]">© MITRA</span>
        </div>
      </footer>
    </div>
  );
}
